import { cn } from '@/lib/utils';

export interface ProductsListSkeletonProps {
  /** How many skeleton cards to render. Defaults to 6 (two rows on
   *  the 3-column grid) which matches the Stitch fallback screen. */
  readonly count?: number;
}

/**
 * Loading skeleton for the products list. Rendered while `useProducts`
 * is fetching. Uses Tailwind's built-in `animate-pulse` over the warm
 * `sand-200` token so it feels native to the rest of the admin UI
 * without requiring a custom `@keyframes shimmer` in index.css.
 *
 * Accessibility: the wrapper carries `role="status"` and
 * `aria-busy="true"` so screen readers announce that content is loading.
 * `aria-hidden` on the individual shimmer blocks keeps chatter down.
 */
export function ProductsListSkeleton({ count = 6 }: ProductsListSkeletonProps) {
  const blockClass = cn('bg-admin-surface-muted animate-pulse');

  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="products-list-skeleton"
      className="space-y-4"
    >
      <div className="hidden md:block">
        <div className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border">
          <div className="bg-admin-table-header grid grid-cols-[minmax(0,2fr)_1fr_1fr_1fr_1fr_3rem] gap-3 border-b px-3 py-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} aria-hidden="true" className={cn('h-3 rounded', blockClass)} />
            ))}
          </div>
          {Array.from({ length: count }).map((_, i) => (
            <div
              key={i}
              className="border-border grid grid-cols-[minmax(0,2fr)_1fr_1fr_1fr_1fr_3rem] items-center gap-3 border-b px-3 py-3 last:border-b-0"
              data-testid={`products-list-skeleton-card-${i}`}
            >
              <div className="flex items-center gap-3">
                <div aria-hidden="true" className={cn('h-12 w-12 rounded-md', blockClass)} />
                <div aria-hidden="true" className={cn('h-4 w-36 rounded', blockClass)} />
              </div>
              <div aria-hidden="true" className={cn('h-4 rounded', blockClass)} />
              <div aria-hidden="true" className={cn('h-4 rounded', blockClass)} />
              <div aria-hidden="true" className={cn('h-5 rounded-full', blockClass)} />
              <div aria-hidden="true" className={cn('h-5 rounded-full', blockClass)} />
              <div aria-hidden="true" className={cn('h-8 w-8 rounded', blockClass)} />
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 md:hidden">
        {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
          <div
            key={i}
            className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border"
            data-testid={`products-list-skeleton-mobile-card-${i}`}
          >
            <div aria-hidden="true" className={cn('h-44 w-full', blockClass)} />
            <div className="flex flex-col gap-2.5 p-4">
              <div aria-hidden="true" className={cn('h-[18px] w-4/5 rounded', blockClass)} />
              <div aria-hidden="true" className={cn('h-3.5 w-1/2 rounded', blockClass)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
